/**
 * MomentsDashboardPage — owner dashboard listing all their Moment Pages,
 * with a Create button and quick-stats per page.
 *
 * Auth: requires an authenticated HustleSuite user with the
 * `hustlemoments` module subscription active on their tier. Gate is
 * enforced by the backend — the frontend just redirects to login on 401.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, QrCode, Users, Image as ImageIcon, ExternalLink, Loader2, Trash2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL ||
             (typeof window !== "undefined" ? window.location.origin : "");

const THEMES = [
  ["restaurant", "Restaurant / Café"],
  ["wedding",    "Wedding"],
  ["hotel",      "Hotel / Resort"],
  ["event",      "Corporate event"],
  ["party",      "Party / Birthday"],
  ["nightclub",  "Nightclub / Bar"],
  ["gym",        "Gym / Fitness"],
  ["salon",      "Salon / Spa"],
  ["retail",     "Retail / Pop-up"],
  ["religious",  "Religious gathering"],
  ["sports",     "Sports / Tournament"],
  ["festival",   "Festival"],
  ["school",     "School / Alumni"],
  ["gallery",    "Gallery / Exhibition"],
  ["generic",    "Something else"],
];

export default function MomentsDashboardPage() {
  const nav = useNavigate();
  const [pages,   setPages]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form,    setForm]    = useState({
    title: "", description: "", theme: "generic", location: "",
  });

  const load = async () => {
    try {
      const r = await fetch(`${API}/api/moments/pages`, {
        credentials: "include",
      });
      if (r.status === 401) {
        toast.error("Please sign in to manage Moment Pages");
        nav("/login?next=/moments/dashboard");
        return;
      }
      if (r.status === 403) {
        toast.error("HustleMoments not active on your plan");
        return;
      }
      const j = await r.json();
      setPages(Array.isArray(j.pages) ? j.pages : []);
    } catch (e) {
      toast.error(`Failed to load pages: ${e.message || e}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const _create = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Give it a title");
      return;
    }
    setCreating(true);
    try {
      const r = await fetch(`${API}/api/moments/pages`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
      toast.success(`Created "${j.title}"`);
      setForm({ title: "", description: "", theme: "generic", location: "" });
      nav(`/moments/pages/${j.page_id}`);
    } catch (e2) {
      toast.error(`Create failed: ${e2.message || e2}`);
    } finally {
      setCreating(false);
    }
  };

  const _delete = async (page_id) => {
    if (!window.confirm("Delete this Moment Page? Existing uploads stay in your account for audit but the QR stops working.")) return;
    try {
      const r = await fetch(`${API}/api/moments/pages/${page_id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setPages((xs) => xs.filter((p) => p.page_id !== page_id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(`Delete failed: ${e.message || e}`);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950 text-stone-900 dark:text-stone-100"
         data-testid="moments-dashboard-root">
      <div className="max-w-5xl mx-auto px-6 py-10">
        <header className="flex items-center justify-between flex-wrap gap-3 mb-8">
          <div>
            <p className="text-xs font-bold tracking-widest text-pink-600 uppercase">Moments</p>
            <h1 className="text-3xl sm:text-4xl font-black">Your Moment Pages</h1>
            <p className="mt-1 text-sm text-stone-500">
              Each page is one QR code guests scan to upload photos, videos & reviews.
            </p>
          </div>
          <Link
            to="/moments"
            className="text-sm text-stone-500 hover:text-stone-800 underline underline-offset-4"
          >
            About Moments
          </Link>
        </header>

        {/* Create card */}
        <form
          onSubmit={_create}
          data-testid="moments-create-form"
          className="rounded-2xl bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 p-5 mb-8 space-y-3"
        >
          <div className="flex items-center gap-2 font-bold">
            <Plus size={18} className="text-pink-600"/>
            Create a new page
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Event or business title *"
              maxLength={140}
              data-testid="moments-create-title"
              className="h-11 rounded-xl border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 px-3"
            />
            <select
              value={form.theme}
              onChange={(e) => setForm({ ...form, theme: e.target.value })}
              data-testid="moments-create-theme"
              className="h-11 rounded-xl border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 px-3"
            >
              {THEMES.map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <input
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              placeholder="Location (optional)"
              maxLength={200}
              data-testid="moments-create-location"
              className="h-11 rounded-xl border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 px-3"
            />
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="One-line description guests see (optional)"
              maxLength={600}
              rows={2}
              data-testid="moments-create-description"
              className="rounded-xl border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 px-3 py-2 resize-none md:col-span-2"
            />
          </div>
          <button
            type="submit"
            disabled={creating}
            data-testid="moments-create-submit"
            className="inline-flex items-center gap-2 rounded-full bg-pink-600 hover:bg-pink-500 text-white px-5 h-11 font-semibold disabled:opacity-60"
          >
            {creating ? <Loader2 className="animate-spin" size={16}/> : <Plus size={16}/>}
            Create Moment Page
          </button>
        </form>

        {/* Pages list */}
        {loading ? (
          <div className="py-16 flex justify-center" data-testid="moments-dashboard-loading">
            <Loader2 className="animate-spin text-stone-400" size={22}/>
          </div>
        ) : pages.length === 0 ? (
          <div className="rounded-2xl bg-white dark:bg-stone-900 border border-dashed border-stone-300 dark:border-stone-700 p-10 text-center"
               data-testid="moments-dashboard-empty">
            <QrCode className="mx-auto text-stone-300" size={40}/>
            <p className="mt-3 text-stone-500">No Moment Pages yet. Create one above.</p>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="moments-dashboard-list">
            {pages.map((p) => (
              <li
                key={p.page_id}
                className="rounded-2xl bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 hover:border-stone-400 dark:hover:border-stone-500 transition p-4 flex items-center gap-4"
                data-testid={`moments-dashboard-row-${p.page_id}`}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: (p.accent_hex || "#f59e0b") + "22", color: p.accent_hex || "#f59e0b" }}
                >
                  <QrCode size={20}/>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/moments/pages/${p.page_id}`}
                      className="font-bold hover:underline underline-offset-4"
                    >
                      {p.title}
                    </Link>
                    {!p.active && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-stone-200 dark:bg-stone-700">
                        PAUSED
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-stone-500 flex items-center gap-3 mt-1 flex-wrap">
                    <span className="capitalize">{p.theme}</span>
                    {p.location && <>· <span>{p.location}</span></>}
                    <span>· <ImageIcon size={11} className="inline mr-0.5"/>{p.media_approved} / {p.media_total}</span>
                  </div>
                </div>
                <a
                  href={p.guest_url}
                  target="_blank" rel="noreferrer"
                  className="hidden sm:inline-flex items-center gap-1 text-xs text-stone-600 hover:text-stone-900 dark:text-stone-400 dark:hover:text-white"
                  data-testid={`moments-dashboard-open-${p.page_id}`}
                >
                  Guest link <ExternalLink size={12}/>
                </a>
                <button
                  type="button"
                  onClick={() => _delete(p.page_id)}
                  className="p-2 rounded-lg text-stone-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20"
                  aria-label="Delete Moment Page"
                  data-testid={`moments-dashboard-delete-${p.page_id}`}
                >
                  <Trash2 size={16}/>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
